// Services.js
import React, { useEffect, useRef, useState } from 'react';

const Services = () => {
  // Keep refs stable and aligned to index (no .push())
  const cardRefs = useRef([]);
  const [animatedCards, setAnimatedCards] = useState([]);

  // Top row (0..2) — on-load stagger
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedCards([0, 1, 2]);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Observer for cards 3..8 — attach immediately on mount
  useEffect(() => {
    const cards = cardRefs.current;
    if (!cards.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idx = Number(entry.target.dataset.index);
          if (idx >= 3 && idx <= 8) {
            setAnimatedCards((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
          }
        });
      },
      {
        root: null,
        threshold: 0.15,
        rootMargin: '0px 0px -15% 0px', // reveal a touch early
      }
    );

    // Observe cards 3..8 if they exist
    for (let i = 3; i <= 8; i++) {
      const el = cards[i];
      if (el) observer.observe(el);
    }

    // One-time catch-up for items already visible before the observer attached
    const vh = window.innerHeight || document.documentElement.clientHeight;
    for (let i = 3; i <= 8; i++) {
      const el = cards[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const alreadyVisible = r.top < vh * 0.9 && r.bottom > 0;
      if (alreadyVisible) {
        setAnimatedCards((prev) => (prev.includes(i) ? prev : [...prev, i]));
      }
    }

    return () => observer.disconnect();
  }, []);

  // Utility for animation class
  const getCardAnimationClass = (index) =>
    animatedCards.includes(index) ? (index < 3 ? 'animate-on-load' : 'animate-on-scroll') : '';

  // Assign refs by fixed index
  const setCardRef = (i) => (el) => {
    if (el) cardRefs.current[i] = el;
  };

  // Minimal geometric SVG icons to match the aesthetic
  const Icons = {
    // Go-To-Market Strategy - Rocket/Launch path
    gtm: (
      <svg viewBox="0 0 64 64" className="service-icon-experimental" aria-hidden="true">
        <path d="M44 8 L52 16 L44 44 L32 56 L20 44 L48 16" fill="none" stroke="currentColor" strokeWidth="3" />
        <circle cx="40" cy="20" r="4" fill="currentColor" />
        <path d="M12 52 L20 44" stroke="currentColor" strokeWidth="3" />
        <path d="M8 44 L16 40" stroke="currentColor" strokeWidth="3" />
      </svg>
    ),
    // Marketing Planning - Calendar/Roadmap
    marketing: (
      <svg viewBox="0 0 64 64" className="service-icon-experimental" aria-hidden="true">
        <rect x="10" y="14" width="44" height="40" rx="4" fill="none" stroke="currentColor" strokeWidth="3" />
        <path d="M10 26 H54" stroke="currentColor" strokeWidth="3" />
        <path d="M20 14 V8 M44 14 V8" stroke="currentColor" strokeWidth="3" />
        <rect x="18" y="32" width="8" height="6" fill="currentColor" />
        <rect x="28" y="32" width="8" height="6" fill="currentColor" />
        <rect x="38" y="32" width="8" height="6" fill="currentColor" />
      </svg>
    ),
    // KOL Activation - Megaphone/Broadcast
    kol: (
      <svg viewBox="0 0 64 64" className="service-icon-experimental" aria-hidden="true">
        <path d="M12 28 L12 36 L20 36 L36 48 L36 16 L20 28 Z" fill="none" stroke="currentColor" strokeWidth="3" />
        <path d="M44 24 C48 28 48 36 44 40" fill="none" stroke="currentColor" strokeWidth="3" />
        <path d="M50 18 C56 24 56 40 50 46" fill="none" stroke="currentColor" strokeWidth="3" />
      </svg>
    ),
    // Content Strategy - Document with lines
    content: (
      <svg viewBox="0 0 64 64" className="service-icon-experimental" aria-hidden="true">
        <path d="M16 8 L16 56 L48 56 L48 20 L36 8 Z" fill="none" stroke="currentColor" strokeWidth="3" />
        <path d="M36 8 L36 20 L48 20" fill="none" stroke="currentColor" strokeWidth="3" />
        <path d="M22 32 H42 M22 40 H38 M22 48 H34" stroke="currentColor" strokeWidth="3" />
      </svg>
    ),
    // Strategic Advisory - Lightbulb/Idea
    advisory: (
      <svg viewBox="0 0 64 64" className="service-icon-experimental" aria-hidden="true">
        <path d="M32 8 C20 8 12 18 12 28 C12 36 18 42 24 46 L24 52 L40 52 L40 46 C46 42 52 36 52 28 C52 18 44 8 32 8" fill="none" stroke="currentColor" strokeWidth="3" />
        <path d="M24 56 H40" stroke="currentColor" strokeWidth="3" />
        <circle cx="32" cy="28" r="4" fill="currentColor" />
      </svg>
    ),
    // Ecosystem & Partnerships - Network/Connected nodes
    ecosystem: (
      <svg viewBox="0 0 64 64" className="service-icon-experimental" aria-hidden="true">
        <circle cx="32" cy="32" r="6" fill="currentColor" />
        <circle cx="16" cy="16" r="5" fill="none" stroke="currentColor" strokeWidth="3" />
        <circle cx="48" cy="16" r="5" fill="none" stroke="currentColor" strokeWidth="3" />
        <circle cx="16" cy="48" r="5" fill="none" stroke="currentColor" strokeWidth="3" />
        <circle cx="48" cy="48" r="5" fill="none" stroke="currentColor" strokeWidth="3" />
        <path d="M20 20 L28 28 M36 28 L44 20 M28 36 L20 44 M36 36 L44 44" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    // Community Growth - People/Group
    community: (
      <svg viewBox="0 0 64 64" className="service-icon-experimental" aria-hidden="true">
        <circle cx="32" cy="18" r="7" fill="none" stroke="currentColor" strokeWidth="3" />
        <path d="M18 52 C18 40 46 40 46 52" fill="none" stroke="currentColor" strokeWidth="3" />
        <circle cx="14" cy="26" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="50" cy="26" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M6 48 C6 40 18 40 20 44" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M58 48 C58 40 46 40 44 44" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    // Campaign Execution - Gear/Settings with check
    campaign: (
      <svg viewBox="0 0 64 64" className="service-icon-experimental" aria-hidden="true">
        <circle cx="32" cy="32" r="12" fill="none" stroke="currentColor" strokeWidth="3" />
        <path d="M32 16 V10 M32 54 V48 M16 32 H10 M54 32 H48" stroke="currentColor" strokeWidth="3" />
        <path d="M22 22 L18 18 M46 46 L42 42 M22 42 L18 46 M46 18 L42 22" stroke="currentColor" strokeWidth="3" />
        <path d="M26 32 L30 36 L38 28" fill="none" stroke="currentColor" strokeWidth="3" />
      </svg>
    ),
    // Launch & Milestone - Flag/Target
    launch: (
      <svg viewBox="0 0 64 64" className="service-icon-experimental" aria-hidden="true">
        <path d="M16 12 L16 56" stroke="currentColor" strokeWidth="3" />
        <path d="M16 12 L48 20 L16 32" fill="none" stroke="currentColor" strokeWidth="3" />
        <circle cx="48" cy="44" r="10" fill="none" stroke="currentColor" strokeWidth="3" />
        <circle cx="48" cy="44" r="4" fill="currentColor" />
      </svg>
    ),
  };

  // 9 services (0..8)
  const services = [
    {
      icon: Icons.gtm,
      title: 'Go-To-Market Strategy',
      description: 'End-to-end planning for launches, repositioning, and major milestones — aligning narrative, timing, and execution.',
    },
    {
      icon: Icons.marketing,
      title: 'Marketing Planning',
      description: 'Strategic marketing roadmaps covering channels, messaging, and campaign structure to support sustainable growth.',
    },
    {
      icon: Icons.kol,
      title: 'KOL Activation & Management',
      description: 'High-signal distribution through trusted, curated creator campaigns focused on quality, relevance, and long-term alignment — not vanity metrics.',
    },
    {
      icon: Icons.content,
      title: 'Content Strategy & Messaging',
      description: 'Development of core narratives, messaging frameworks, and content direction that guide all outbound efforts.',
    },
    {
      icon: Icons.advisory,
      title: 'Strategic Advisory',
      description: 'Hands-on support across positioning, partnerships, and ecosystem navigation based on real market experience.',
    },
    {
      icon: Icons.ecosystem,
      title: 'Ecosystem & Partnership Connections',
      description: 'Warm introductions to creators, funds, platforms, and partners that accelerate growth and opportunity.',
    },
    {
      icon: Icons.community,
      title: 'Community Growth & Engagement',
      description: 'Building and activating communities through structured programs, content, and ongoing engagement.',
    },
    {
      icon: Icons.campaign,
      title: 'Campaign Execution & Optimization',
      description: 'Execution support with ongoing optimization, feedback loops, and performance review to ensure outcomes, not just output.',
    },
    {
      icon: Icons.launch,
      title: 'Launch & Milestone Support',
      description: 'Focused support around key moments such as token launches, product releases, testnets, and major announcements.',
    },
  ];

  return (
    <section id="services" className="services-experimental">
      <div className="container">
        <div className="section-header-experimental">
          <div className="header-decoration">
            <div className="decoration-line" />
            <h2>Our Services</h2>
            <div className="decoration-line" />
          </div>
          <p>Strategic solutions for gaming and Web3 success</p>
        </div>

        <div className="services-grid-experimental">
          {services.map((service, index) => (
            <div
              key={index}
              ref={setCardRef(index)}
              data-index={index}
              className={`service-card-experimental ${getCardAnimationClass(index)}`}
              style={{
                // keep your top row stagger; others can share a small delay for cohesion
                transitionDelay: index < 3 ? `${index * 0.2}s` : '0.3s',
              }}
            >
              <div className="service-icon-wrapper">{service.icon}</div>

              <div className="service-content">
                <h3>{service.title}</h3>
                <div className="title-underline" />
                <p>{service.description}</p>
              </div>

              <div className="service-number">0{index + 1}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
